import {
    Theme,
    ComponentsProps,
    ComponentsOverrides,
    ComponentsVariants,
    alpha,
} from '@mui/material';
import { StateLayer, getStateLayerColor } from '../utils/getStayeLayerColor';
import { colorVariants } from '@M3/utils/data';
import { getInverseColor } from '@M3/utils/general';

declare module '@mui/material/Button' {
    interface ButtonPropsVariantOverrides {
        elevated: true;
        filled: true;
        tonal: true;
        outlined: true;
        text: true;
        contained: false;
    }
    interface ButtonPropsColorOverrides {
        tertiary: true;
        surface: true;
    }
    interface ButtonPropsSizeOverrides {
        extraLarge: true;
    }
}

interface M3Button {
    MuiButton: {
        defaultProps?: ComponentsProps['MuiButton'];
        styleOverrides?: ComponentsOverrides<Theme>['MuiButton'];
        variants?: ComponentsVariants['MuiButton'];
    };
}

export const getButton = (theme: Theme): M3Button => {
    const { palette } = theme;

    const getFilledColorStyles = (color: string) => {
        return {
            backgroundColor: palette[color].main,
            color: palette[getInverseColor(color)].main,
            boxShadow: theme.shadows[0],
            '&.Mui-disabled': {
                backgroundColor: alpha(palette.onSurface.main, 0.12),
                color: alpha(palette.onSurface.main, 0.38),
                boxShadow: theme.shadows[0],
            },
            '&:hover': {
                backgroundColor: getStateLayerColor(
                    StateLayer.Hover,
                    palette[color].main,
                    palette[getInverseColor(color)].main,
                ),
                boxShadow: theme.shadows[1],
            },
            '&:focus': {
                backgroundColor: getStateLayerColor(
                    StateLayer.Focus,
                    palette[color].main,
                    palette[getInverseColor(color)].main,
                ),
                boxShadow: theme.shadows[0],
            },
            '&:active': {
                backgroundColor: getStateLayerColor(
                    StateLayer.Press,
                    palette[color].main,
                    palette[getInverseColor(color)].main,
                ),
                boxShadow: theme.shadows[0],
            },
        };
    };

    const getElevatedColorStyles = (color: string) => {
        return {
            boxShadow: theme.shadows[1],
            backgroundColor: palette.surfaceContainerLow.main,
            color: palette[color].main,
            '&:hover': {
                background: getStateLayerColor(
                    StateLayer.Hover,
                    palette.surfaceContainerLow.main,
                    palette[color].main,
                ),
                boxShadow: theme.shadows[2],
            },
            '&:focus': {
                background: getStateLayerColor(
                    StateLayer.Focus,
                    palette.surfaceContainerLow.main,
                    palette[color].main,
                ),
            },
            '&:active': {
                background: getStateLayerColor(
                    StateLayer.Press,
                    palette.surfaceContainerLow.main,
                    palette[color].main,
                ),
            },
            '&.Mui-disabled': {
                backgroundColor: alpha(palette.onSurface.main, 0.12),
                color: alpha(palette.onSurface.main, 0.38),
                boxShadow: theme.shadows[0],
            },
        };
    };

    const getTonalColorStyles = (color: string) => {
        const colorContainer = color + 'Container';

        return {
            backgroundColor: palette[colorContainer].main,
            color: palette[getInverseColor(colorContainer)].main,
            boxShadow: theme.shadows[0],
            '&.Mui-disabled': {
                backgroundColor: alpha(palette.onSurface.main, 0.12),
                color: alpha(palette.onSurface.main, 0.38),
                boxShadow: theme.shadows[0],
            },
            '&:hover': {
                backgroundColor: getStateLayerColor(
                    StateLayer.Hover,
                    palette[colorContainer].main,
                    palette[getInverseColor(colorContainer)].main,
                ),
                boxShadow: theme.shadows[1],
            },
            '&:focus': {
                backgroundColor: getStateLayerColor(
                    StateLayer.Focus,
                    palette[colorContainer].main,
                    palette[getInverseColor(colorContainer)].main,
                ),
                boxShadow: theme.shadows[0],
            },
            '&:active': {
                backgroundColor: getStateLayerColor(
                    StateLayer.Press,
                    palette[colorContainer].main,
                    palette[getInverseColor(colorContainer)].main,
                ),
                boxShadow: theme.shadows[0],
            },
        };
    };

    const getOutlineColorStyles = (color: string) => {
        return {
            color: palette[color].main,
            borderColor: palette.outline.main,
            borderWidth: '1px',
            boxShadow: theme.shadows[0],
            '&.Mui-disabled': {
                borderColor: alpha(palette.onSurface.main, 0.12),
                color: alpha(palette.onSurface.main, 0.38),
            },
            '&:hover': {
                backgroundColor: getStateLayerColor(
                    StateLayer.Hover,
                    palette.surface.main,
                    palette[color].main,
                ),
                borderColor: getStateLayerColor(
                    StateLayer.Hover,
                    palette.outline.main,
                    palette[color].main,
                ),
            },
            '&:focus': {
                backgroundColor: getStateLayerColor(
                    StateLayer.Focus,
                    palette.surface.main,
                    palette[color].main,
                ),
                borderColor: getStateLayerColor(
                    StateLayer.Focus,
                    palette[color].main,
                    palette[color].main,
                ),
            },
            '&:active': {
                backgroundColor: getStateLayerColor(
                    StateLayer.Press,
                    palette.surface.main,
                    palette[color].main,
                ),
                borderColor: getStateLayerColor(
                    StateLayer.Press,
                    palette.outline.main,
                    palette[color].main,
                ),
            },
        };
    };

    const getTextColorStyles = (color: string) => {
        return {
            backgroundColor: 'transparent',
            color: palette[color].main,
            boxShadow: theme.shadows[0],
            padding: '5px 15px',
            '&.Mui-disabled': {
                color: alpha(palette.onSurface.main, 0.38),
            },
            '&:hover': {
                backgroundColor: getStateLayerColor(
                    StateLayer.Hover,
                    palette.surface.main,
                    palette[color].main,
                ),
            },
            '&:focus': {
                backgroundColor: getStateLayerColor(
                    StateLayer.Focus,
                    palette.surface.main,
                    palette[color].main,
                ),
            },
            '&:active': {
                backgroundColor: getStateLayerColor(
                    StateLayer.Press,
                    palette.surface.main,
                    palette.primary.main,
                ),
            },
        };
    };

    const getFilledColorVariants = () => {
        return colorVariants.reduce((acc, color) => {
            acc.push({
                props: { variant: 'filled', color },
                style: getFilledColorStyles(color),
            });
            return acc;
        }, []);
    };

    const getElevatedColorVariants = () => {
        return colorVariants.reduce((acc, color) => {
            acc.push({
                props: { variant: 'elevated', color },
                style: getElevatedColorStyles(color),
            });
            return acc;
        }, []);
    };

    const getTonalColorVariants = () => {
        return colorVariants.reduce((acc, color) => {
            acc.push({
                props: { variant: 'tonal', color },
                style: getTonalColorStyles(color),
            });
            return acc;
        }, []);
    };

    const getOutlineColorVariants = () => {
        return colorVariants.reduce((acc, color) => {
            acc.push({
                props: { variant: 'outlined', color },
                style: getOutlineColorStyles(color),
            });
            return acc;
        }, []);
    };

    const getTextColorVariants = () => {
        return colorVariants.reduce((acc, color) => {
            acc.push({
                props: { variant: 'text', color },
                style: getTextColorStyles(color),
            });
            return acc;
        }, []);
    };

    return {
        MuiButton: {
            styleOverrides: {
                root: {
                    borderRadius: '30px',
                    textTransform: 'none',
                    '&:has(>svg)': {
                        padding: '8px',
                        // borderRadius: '50%',
                        minWidth: '1em',
                        minHeight: '1em',
                    },
                },
                sizeLarge: {
                    height: 40,
                    ...theme.typography.labelLarge,
                    paddingRight: 24,
                    paddingLeft: 24,
                    '&:has(>.MuiButton-startIcon)': {
                        paddingLeft: 16,
                    },
                    '&:has(>.MuiButton-endIcon)': {
                        paddingRight: 16,
                    },
                },
                sizeExtraLarge: {
                    height: 48,
                    ...theme.typography.titleMedium,
                    paddingRight: 28,
                    paddingLeft: 28,
                    '&:has(>.MuiButton-startIcon)': {
                        paddingLeft: 20,
                    },
                    '&:has(>.MuiButton-endIcon)': {
                        paddingRight: 20,
                    },
                },
                sizeMedium: {
                    height: 32,
                    ...theme.typography.labelMedium,
                    paddingRight: 20,
                    paddingLeft: 20,
                    '&:has(>.MuiButton-startIcon)': {
                        paddingLeft: 12,
                    },
                    '&:has(>.MuiButton-endIcon)': {
                        paddingRight: 12,
                    },
                },
                sizeSmall: {
                    height: 28,
                    ...theme.typography.labelSmall,
                    paddingRight: 16,
                    paddingLeft: 16,
                    '&:has(>.MuiButton-startIcon)': {
                        paddingLeft: 8,
                    },
                    '&:has(>.MuiButton-endIcon)': {
                        paddingRight: 8,
                    },
                },
            },
            defaultProps: {
                size: 'large',
                // color: 'primary',
                variant: 'filled',
            },
            variants: [
                ...getElevatedColorVariants(),
                ...getFilledColorVariants(),
                ...getTonalColorVariants(),
                ...getOutlineColorVariants(),
                ...getTextColorVariants(),
            ],
        },
    };
};
